import { TransactionCurrency } from '@contracts/payment/Currency';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import Info from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Skeleton from '@mui/material/Skeleton';
import { useAvailableCurrencies } from '@src/clients/financialSettings/hooks/useAvailableCurrencies';
import { Button } from '@src/common/components/button/Button';
import type { RowDefinition } from '@src/common/components/SimpleTable';
import { Stack } from '@src/common/components/Stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import { Tooltip } from '@src/common/components/Tooltip';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { MakePaymentButton } from '@src/orders/components/payment/MakePaymentButton';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import type { Transaction } from '@src/orders/domain/Transaction';
import { useGetOrderTransactions } from '@src/orders/hooks/useGetOrderTransactions';
import { useRefreshInvoice } from '@src/orders/hooks/useReviewableOrder';
import { SettlePaymentModal } from '@src/orders/review/SettlePaymentModal';
import { TransactionStatusCell } from '@src/orders/review/TransactionStatusCell';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  order: ReviewableOrder;
  maxBillable: number;
  disablePayment?: boolean;
}

export const OrderTransactions = ({
  order,
  maxBillable,
  disablePayment,
}: Props) => {
  const { t } = useTranslation();
  const [transactionToSettle, setTransactionToSettle] = useState<
    Transaction | undefined
  >();
  const {
    transactions,
    noTransactionFound,
    isLoadingTransactions,
    refetchTransactions,
  } = useGetOrderTransactions({ orderId: order.id });
  const { refreshInvoice } = useRefreshInvoice({ orderId: order.id });
  const { currencies: availableCurrencies } = useAvailableCurrencies();

  const handlePaymentSuccess = async () => {
    await refetchTransactions();
    refreshInvoice();
  };

  const columnsName = [
    { name: t('fields.type') },
    { name: t('date') },
    { name: t('transactionId') },
    { name: t('fields.companyName') },
    { name: t('card') },
    { name: t('amount') },
    { name: '' },
  ];

  const lastColumn = (transaction: Transaction) => {
    if (transaction.canBeSettledBy(order.billTo.id)) {
      return (
        <Button
          size="small"
          className={action}
          onClick={() => setTransactionToSettle(transaction)}
          disabled={disablePayment}
        >
          {t('settle')}
        </Button>
      );
    }

    if (!transaction.isCreatedBy(order.billTo.id)) {
      return <SettlementDisabledInfo tooltipText={t('preauthDisabledInfo')} />;
    }

    if (!availableCurrencies.includes(transaction.currency)) {
      return <SettlementDisabledInfo tooltipText={t('noCurrencyAvailable')} />;
    }

    if (transaction.isLastOperation()) {
      return <TransactionStatusCell transaction={transaction} />;
    }
  };

  const buildRow = (
    transaction: Transaction,
    { isChild = false }: { isChild?: boolean } = {},
  ) => ({
    id: `order-transaction-${transaction.id}`,
    isDimmed: !transaction.isCreatedBy(order.billTo.id),
    columnsContent: [
      `${isChild ? '↳ ' : ''}${t(`transactionType.${transaction.type}`)}`,
      transaction.date.formatDate('dd/MM/yyyy'),
      transaction.transactionId,
      transaction.client.name,
      transaction.card,
      `${transaction.currency} $${roundToTwoDecimalsString(transaction.amount)}`,
      lastColumn(transaction),
    ],
  });

  const rows: (RowDefinition & { isDimmed: boolean })[] = transactions.flatMap(
    (transaction) => [
      buildRow(transaction),
      ...transaction.children.map((child) =>
        buildRow(child, { isChild: true }),
      ),
    ],
  );

  const showNoTransactionFound = () => {
    return (
      <div className={noTransactionFoundStyle}>
        <Typography variant="body2">{t('noTransactionFound')}</Typography>
      </div>
    );
  };

  const loadingRow = () => {
    return (
      <TableRow data-testid="loading-row">
        {columnsName.map((_, index) => (
          <TableCell key={`skeleton-${index}`} className={cell}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const getPaymentMandatoryCurrency = () => {
    if (!order.billingCurrency) {
      return undefined;
    }
    if (order.billingCurrency === 'ALL') {
      return undefined;
    }

    if (
      !availableCurrencies.includes(TransactionCurrency[order.billingCurrency])
    ) {
      return undefined;
    }
    if (order.billingCurrency === 'CAD') {
      return TransactionCurrency.CAD;
    }
    if (order.billingCurrency === 'USD') {
      return TransactionCurrency.USD;
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" style={{ padding: theme.spacing['2'] }}>
          {t('transactions')}
        </Typography>

        <MakePaymentButton
          orderId={order.id}
          clientId={order.billTo.id}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={refetchTransactions}
          blockedCurrency={getPaymentMandatoryCurrency()}
          disabled={order.isReviewing() || disablePayment}
          maxBillable={maxBillable}
        />
      </Stack>

      <Table size="small" aria-label={t('transactions')}>
        <TableHead>
          <TableRow>
            {columnsName.map((column) => (
              <TableCell key={column.name} className={header}>
                {column.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoadingTransactions && loadingRow()}

          {!isLoadingTransactions &&
            rows.map(({ id, columnsContent, isDimmed }) => (
              <TableRow key={`row-${id}`} hover>
                {columnsContent.map((content, i) => (
                  <TableCell
                    key={`row-${id}-column-${i}`}
                    className={cell}
                    sx={{
                      color:
                        isDimmed && i !== columnsContent.length - 1
                          ? theme.palette.secondary.secondaryFixedDim
                          : 'inherit',
                    }}
                  >
                    {content}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {noTransactionFound && showNoTransactionFound()}
      {transactionToSettle && (
        <SettlePaymentModal
          orderId={order.id}
          onClose={() => setTransactionToSettle(undefined)}
          preauthorization={transactionToSettle}
          clientId={order.billTo.id}
          maxBillable={maxBillable}
        />
      )}
    </>
  );
};

const SettlementDisabledInfo = ({ tooltipText }: { tooltipText: string }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip
      title={tooltipText}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Stack direction="row" gap={1}>
        {hovered && <Info style={{ fill: '#0F61AC' }} />}
        {!hovered && <InfoOutlinedIcon style={{ fill: '#0F61AC' }} />}

        <Typography style={theme.typography.body.large}>{t('NA')}</Typography>
      </Stack>
    </Tooltip>
  );
};

const border = theme.table.border.primary;

const header = css({
  borderTop: border,
  borderBottom: theme.table.border.secondary,
  '&:not(:last-child)': {
    borderRight: border,
  },
});

const cell = css({
  '&:not(:last-child)': {
    borderRight: border,
  },
});

const action = css({
  width: '100%',
  padding: '4px 16px',
  backgroundColor: '#DDE1F9',
  color: 'black',
});

const noTransactionFoundStyle = css({
  display: 'flex',
  justifyContent: 'center',
  paddingBottom: theme.spacing['4'],
  width: '100%',
  borderBottom: `1px solid ${theme.palette.surface.surfaceContainerHighest}`,
});
